import type { OverviewDto } from '../../shared/api';
import type { NewSplit } from './views/splits';
import type { NewTip } from './views/tips';
import type { NewSheetState } from './views/newsheet';
import type { Draft } from './views/drawer';
import type { PartnerUi, SettingsPage } from './views/settings';
import type { FlowState } from './views/card-flow';

/** The category drawer, while open: its draft, and "Discard changes?" with what to do after. */
export interface DrawerState {
  id: string;
  draft: Draft;
  confirm: boolean;
  after?: () => void;
}

export type Tab = 'overview' | 'activity' | 'insights' | 'budget' | 'settings';

export interface SheetState {
  id: string | null;
  cat: string | null;
  /** The planned item picked in the Item picker. */
  item?: string | null;
  armed: boolean;
  /** Set when the sheet was opened from a Review item; saving resolves it. */
  reviewId?: string;
  /** The add sheet's purchase once saved: a retry after a later step failed never saves it twice. */
  createdId?: string;
  /** The Idempotency-Key of this submit (POST /api/transactions), kept for its retries. */
  submitKey?: string;
}

export const ui: {
  // Screen fields (tab, filter, settingsPage, categoryId, from, learned, settle): written only by
  // go() in main.ts, from nextNav() in nav.ts.
  tab: Tab;
  filter: string;
  sheet: SheetState | null;
  undo: (() => void) | null;
  toastTimer: ReturnType<typeof setTimeout> | undefined;
  /** The Settings screen open over the menu (null: the menu itself). */
  settingsPage: SettingsPage | null;
  /** The add-a-card flow, full screen over everything (memory only). */
  cardFlow: FlowState | null;
  /** The category screen, when open (over the current tab). */
  categoryId: string | null;
  /** Month chips picked in the item sheet. */
  itemMonths: number[];
  /** The top-level tab Settings was opened from (its Back returns there). */
  from: Exclude<Tab, 'settings'>;
  /** Activity's card filter: 'all' or a card id. */
  card: string;
  /** The category drawer (Budget tab), when open. */
  drawer: DrawerState | null;
  /** The item editor was opened from the drawer: closing it goes back to the drawer with this. */
  drawerReturn: DrawerState | null;
  /** Category chips picked on the New purchases card, per purchase (before Confirm). */
  picks: Map<string, string>;
  /** New purchases rows with "Split this bill" on (off when absent). */
  newSplits: Map<string, NewSplit>;
  /** New purchases rows with "Add tip" open (closed when absent). */
  newTips: Map<string, NewTip>;
  /** New purchases rows with "Paid from savings" open: the fund picked ('' none yet). */
  newFunds: Map<string, string>;
  /** New purchases rows with an item picked in their Edit sheet (null: none). */
  newItems: Map<string, string | null>;
  /** The Edit sheet's "Paid from savings" switch per row (absent: as the purchase is). */
  newFundOn: Map<string, boolean>;
  /** "What was this for?" typed in a New purchases row's Edit sheet (before Confirm). */
  newNotes: Map<string, string>;
  /** A New purchases row's amount typed in its Edit sheet (auto-logged charges only; null: not an amount). */
  newAmounts: Map<string, number | null>;
  /** The Edit sheet of a New purchases row, when open. */
  newSheet: NewSheetState | null;
  /** "What Hush has learned" is open (over Settings), with its search text. */
  learned: { search: string } | null;
  /** Settings, Partner: the link options are open, and the code just made (shown once). */
  partner: PartnerUi;
  /** The Splits screen is open (over whatever was showing). */
  settle: boolean;
  /** The month being viewed (null: the active month). Written only by go(). */
  month: string | null;
  /** Lines unticked on the Splits screen (memory only): they stay outstanding and carry over. */
  settleOff: Set<string>;
  /** The Splits screen's History: open entries and "Show older" (memory only). */
  settleHistory: { open: Set<string>; older: boolean };
} = {
  tab: 'overview',
  filter: 'all',
  sheet: null,
  undo: null,
  toastTimer: undefined,
  settingsPage: null,
  cardFlow: null,
  categoryId: null,
  itemMonths: [],
  from: 'overview',
  card: 'all',
  drawer: null,
  drawerReturn: null,
  picks: new Map(),
  newSplits: new Map(),
  newTips: new Map(),
  newFunds: new Map(),
  newItems: new Map(),
  newFundOn: new Map(),
  newNotes: new Map(),
  newAmounts: new Map(),
  newSheet: null,
  learned: null,
  partner: { open: false, code: null, expiresAt: null },
  settle: false,
  month: null,
  settleOff: new Set(),
  settleHistory: { open: new Set(), older: false },
};

export const store: { data: OverviewDto | null } = { data: null };

export const catName = (id: string | null) => store.data?.categories.find((k) => k.id === id)?.name ?? '';
